import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import AlertMessage from "components/UI/AlertMessage";
import Unauthorized from "components/UI/Unauthorized";
import { usePermissions } from "context/PermissionsContext";
import useAPI from "hooks/useAPI";
import useAlert from "hooks/useAlert";
import { useTranslation } from "react-i18next";
import {
  Card,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  IconButton,
  Tooltip,
  Skeleton,
  Chip,
  Divider,
  CircularProgress,
} from "@mui/material";
import SoftTable from "examples/Tables/Table";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SearchIcon from "@mui/icons-material/Search";
import StorageIcon from "@mui/icons-material/Storage";
import TopicIcon from "@mui/icons-material/Topic";
import RefreshIcon from "@mui/icons-material/Refresh";

// ─── helpers ─────────────────────────────────────────────────────────────────

const StatCard = ({ icon, label, value, color }) => (
  <Card
    sx={{
      p: 2.5,
      display: "flex",
      alignItems: "center",
      gap: 2,
      border: "1px solid rgba(0,0,0,0.06)",
      boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      borderRadius: "12px",
    }}
  >
    <SoftBox
      sx={{
        width: 48,
        height: 48,
        borderRadius: "12px",
        background: `linear-gradient(135deg, ${color}22 0%, ${color}44 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {React.cloneElement(icon, { sx: { fontSize: 22, color } })}
    </SoftBox>
    <SoftBox>
      <SoftTypography variant="caption" color="secondary" sx={{ fontWeight: 500 }}>
        {label}
      </SoftTypography>
      <SoftTypography variant="h5" fontWeight="bold" sx={{ lineHeight: 1.2, mt: 0.2 }}>
        {value ?? <Skeleton width={40} />}
      </SoftTypography>
    </SoftBox>
  </Card>
);

StatCard.defaultProps = { value: null };
StatCard.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  color: PropTypes.string.isRequired,
};

// ─── main component ───────────────────────────────────────────────────────────

function Topics() {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const { api } = useAPI();
  const { errorState, showAlert } = useAlert();
  const navigate = useNavigate();

  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Add dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [newTopic, setNewTopic] = useState({ name: "", num_partitions: 1, replication_factor: 1 });
  const [addErrors, setAddErrors] = useState({});

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [topicToDelete, setTopicToDelete] = useState(null);

  const pagesList = [{ name: t("lang.topics"), route: "" }];

  if (!hasPermission("can_view_kafka")) return <Unauthorized />;

  // ─── fetch ──────────────────────────────────────────────────────────────────

  const fetchTopics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/topics/");
      console.log("res", res);
      setTopics(res.data.topics || []);
    } catch (err) {
      console.log("err", err);
      showAlert("error", err?.response?.data?.message || t("lang.something_went_wrong"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTopics();
  }, []);

  // ─── derived ────────────────────────────────────────────────────────────────

  const filtered = topics.filter((t) => t.name.toLowerCase().includes(search.toLowerCase()));

  const totalPartitions = topics.reduce((acc, t) => acc + (t.partitions || 0), 0);

  // ─── add ────────────────────────────────────────────────────────────────────

  const validateAdd = () => {
    const errs = {};
    if (!newTopic.name.trim()) errs.name = "Topic name is required.";
    if (!Number.isInteger(Number(newTopic.num_partitions)) || Number(newTopic.num_partitions) < 1)
      errs.num_partitions = "Must be a positive integer.";
    if (
      !Number.isInteger(Number(newTopic.replication_factor)) ||
      Number(newTopic.replication_factor) < 1
    )
      errs.replication_factor = "Must be a positive integer.";
    return errs;
  };

  const handleAddOpen = () => {
    setNewTopic({ name: "", num_partitions: 1, replication_factor: 1 });
    setAddErrors({});
    setAddOpen(true);
  };

  const handleAddClose = () => {
    if (addLoading) return;
    setAddOpen(false);
  };

  const handleAddSubmit = async () => {
    const errs = validateAdd();
    if (Object.keys(errs).length) {
      setAddErrors(errs);
      return;
    }
    setAddLoading(true);
    try {
      await api.post("/topics/", {
        name: newTopic.name.trim(),
        num_partitions: Number(newTopic.num_partitions),
        replication_factor: Number(newTopic.replication_factor),
      });
      showAlert("success", `Topic "${newTopic.name.trim()}" created successfully.`);
      setAddOpen(false);
      fetchTopics();
    } catch (err) {
      showAlert("error", err?.response?.data?.message || t("lang.something_went_wrong"));
    } finally {
      setAddLoading(false);
    }
  };

  // ─── delete ─────────────────────────────────────────────────────────────────

  const handleDeleteOpen = (topic) => {
    setTopicToDelete(topic);
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    if (deleteLoading) return;
    setDeleteOpen(false);
    setTopicToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!topicToDelete) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/topics/${topicToDelete.name}/`);
      showAlert("success", `Topic "${topicToDelete.name}" deleted successfully.`);
      setDeleteOpen(false);
      setTopicToDelete(null);
      fetchTopics();
    } catch (err) {
      showAlert("error", err?.response?.data?.message || t("lang.something_went_wrong"));
    } finally {
      setDeleteLoading(false);
    }
  };

  // ─── render ─────────────────────────────────────────────────────────────────

  return (
    <DashboardLayout>
      <DashboardNavbar pagesList={pagesList} />

      {/* ── stat cards ── */}
      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<TopicIcon />}
            label="Total Topics"
            value={loading ? null : topics.length}
            color="#5e72e4"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<StorageIcon />}
            label="Total Partitions"
            value={loading ? null : totalPartitions}
            color="#2dce89"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<SearchIcon />}
            label="Filtered Results"
            value={loading ? null : filtered.length}
            color="#fb6340"
          />
        </Grid>
      </Grid>

      {/* ── main card ── */}
      <Card
        sx={{
          border: "1px solid rgba(0,0,0,0.06)",
          boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
          borderRadius: "16px",
          overflow: "hidden",
        }}
      >
        {/* card header */}
        <SoftBox
          px={3}
          py={2.5}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1.5}
        >
          <SoftBox display="flex" alignItems="center" gap={1.5}>
            <SoftBox
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                background: "linear-gradient(135deg, #5e72e4 0%, #825ee4 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TopicIcon sx={{ color: "#fff", fontSize: 20 }} />
            </SoftBox>
            <SoftBox>
              <SoftTypography variant="h6" fontWeight="bold" lineHeight={1.2}>
                Kafka Topics
              </SoftTypography>
              <SoftTypography variant="caption" color="secondary">
                Manage your Kafka topics
              </SoftTypography>
            </SoftBox>
          </SoftBox>

          <SoftBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            {/* search */}
            <SoftInput
              placeholder="Search topics…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              icon={{ component: <SearchIcon sx={{ fontSize: 18 }} />, direction: "left" }}
              sx={{ width: 220 }}
            />

            {/* refresh */}
            <Tooltip title="Refresh" placement="top">
              <IconButton
                onClick={fetchTopics}
                disabled={loading}
                sx={{
                  border: "1px solid rgba(0,0,0,0.12)",
                  borderRadius: "10px",
                  p: 1,
                }}
              >
                {loading ? (
                  <CircularProgress size={18} thickness={4} />
                ) : (
                  <RefreshIcon sx={{ fontSize: 20, color: "#5e72e4" }} />
                )}
              </IconButton>
            </Tooltip>

            {/* add */}
            <SoftButton
              variant="gradient"
              color="info"
              startIcon={<AddIcon />}
              onClick={handleAddOpen}
              sx={{ borderRadius: "10px", px: 2.5, py: 1, fontWeight: 600 }}
            >
              Add Topic
            </SoftButton>
          </SoftBox>
        </SoftBox>

        <Divider sx={{ m: 0, borderColor: "rgba(0,0,0,0.06)" }} />

        {/* table */}
        {loading ? (
          <SoftBox px={3} py={4} display="flex" flexDirection="column" gap={1.5}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={44} sx={{ borderRadius: "8px" }} />
            ))}
          </SoftBox>
        ) : filtered.length === 0 ? (
          <SoftBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={7}
            gap={1.5}
          >
            <SoftBox
              sx={{
                width: 64,
                height: 64,
                borderRadius: "16px",
                background: "rgba(94,114,228,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TopicIcon sx={{ fontSize: 32, color: "#5e72e4" }} />
            </SoftBox>
            <SoftTypography variant="h6" color="secondary" fontWeight="medium">
              {search ? "No topics match your search." : "No topics found."}
            </SoftTypography>
            {!search && (
              <SoftButton
                variant="outlined"
                color="info"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddOpen}
                sx={{ borderRadius: "8px", mt: 0.5 }}
              >
                Create your first topic
              </SoftButton>
            )}
          </SoftBox>
        ) : (
          <>
            <SoftTable
              columns={[
                { name: "#", align: "left", width: "6%" },
                { name: "topic name", align: "left", width: "42%" },
                { name: "partitions", align: "left", width: "18%" },
                { name: "status", align: "left", width: "18%" },
                { name: "actions", align: "center", width: "16%" },
              ]}
              rows={filtered.map((topic, idx) => ({
                hasBorder: true,
                "#": (
                  <SoftTypography variant="caption" color="secondary" fontWeight="medium">
                    {idx + 1}
                  </SoftTypography>
                ),
                "topic name": (
                  <SoftBox display="flex" alignItems="center" gap={1.2}>
                    <SoftBox
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        background: "linear-gradient(135deg, #5e72e422 0%, #825ee422 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <TopicIcon sx={{ fontSize: 15, color: "#5e72e4" }} />
                    </SoftBox>
                    <SoftTypography variant="button" fontWeight="medium" color="dark">
                      {topic.name}
                    </SoftTypography>
                  </SoftBox>
                ),
                partitions: (
                  <Chip
                    label={`${topic.partitions} partition${topic.partitions !== 1 ? "s" : ""}`}
                    size="small"
                    sx={{
                      background: "rgba(45,206,137,0.12)",
                      color: "#1aae6f",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 24,
                      borderRadius: "6px",
                    }}
                  />
                ),
                status: (
                  <Chip
                    label={topic.error ? "Error" : "Healthy"}
                    size="small"
                    sx={{
                      background: topic.error ? "rgba(251,99,64,0.12)" : "rgba(94,114,228,0.1)",
                      color: topic.error ? "#f5365c" : "#5e72e4",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 24,
                      borderRadius: "6px",
                    }}
                  />
                ),
                actions: (
                  <SoftBox display="flex" alignItems="center" justifyContent="center" gap={0.8}>
                    <Tooltip title="View details" placement="top">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/topic/${encodeURIComponent(topic.name)}`)}
                        sx={{
                          border: "1px solid rgba(94,114,228,0.25)",
                          borderRadius: "8px",
                          p: 0.7,
                          "&:hover": {
                            background: "rgba(94,114,228,0.08)",
                            borderColor: "#5e72e4",
                          },
                          transition: "all 0.2s",
                        }}
                      >
                        <VisibilityOutlinedIcon sx={{ fontSize: 17, color: "#5e72e4" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete topic" placement="top">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteOpen(topic)}
                        sx={{
                          border: "1px solid rgba(245,54,92,0.2)",
                          borderRadius: "8px",
                          p: 0.7,
                          "&:hover": {
                            background: "rgba(245,54,92,0.08)",
                            borderColor: "#f5365c",
                          },
                          transition: "all 0.2s",
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 17, color: "#f5365c" }} />
                      </IconButton>
                    </Tooltip>
                  </SoftBox>
                ),
              }))}
            />
            <SoftBox
              px={3}
              py={1.8}
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              sx={{ borderTop: "1px solid rgba(0,0,0,0.05)" }}
            >
              <SoftTypography variant="caption" color="secondary">
                Showing <strong>{filtered.length}</strong> of <strong>{topics.length}</strong>{" "}
                topics
              </SoftTypography>
            </SoftBox>
          </>
        )}
      </Card>

      {/* ── Add Topic Dialog ── */}
      <Dialog
        open={addOpen}
        onClose={handleAddClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "16px", overflow: "hidden" },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #5e72e4 0%, #825ee4 100%)",
            color: "#fff",
            py: 2.5,
            px: 3,
          }}
        >
          <SoftBox display="flex" alignItems="center" gap={1.2}>
            <AddIcon sx={{ fontSize: 22 }} />
            <SoftTypography variant="h6" fontWeight="bold" sx={{ color: "#fff" }}>
              Create New Topic
            </SoftTypography>
          </SoftBox>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 1, px: 3 }}>
          <SoftBox my={2.5}>
            <SoftTypography
              variant="caption"
              fontWeight={600}
              sx={{ color: "#344767", mb: 0.8, display: "block" }}
            >
              Topic Name <span style={{ color: "#f5365c" }}>*</span>
            </SoftTypography>
            <SoftInput
              placeholder="e.g. my-service-events"
              fullWidth
              value={newTopic.name}
              onChange={(e) => {
                setNewTopic((p) => ({ ...p, name: e.target.value }));
                setAddErrors((p) => ({ ...p, name: undefined }));
              }}
              error={!!addErrors.name}
            />
            {addErrors.name && (
              <SoftTypography
                variant="caption"
                sx={{ color: "#f5365c", mt: 0.5, display: "block" }}
              >
                {addErrors.name}
              </SoftTypography>
            )}
          </SoftBox>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <SoftTypography
                variant="caption"
                fontWeight={600}
                sx={{ color: "#344767", mb: 0.8, display: "block" }}
              >
                Partitions <span style={{ color: "#f5365c" }}>*</span>
              </SoftTypography>
              <SoftInput
                type="number"
                placeholder="1"
                fullWidth
                value={newTopic.num_partitions}
                inputProps={{ min: 1 }}
                onChange={(e) => {
                  setNewTopic((p) => ({ ...p, num_partitions: e.target.value }));
                  setAddErrors((p) => ({ ...p, num_partitions: undefined }));
                }}
                error={!!addErrors.num_partitions}
              />
              {addErrors.num_partitions && (
                <SoftTypography
                  variant="caption"
                  sx={{ color: "#f5365c", mt: 0.5, display: "block" }}
                >
                  {addErrors.num_partitions}
                </SoftTypography>
              )}
            </Grid>
            <Grid item xs={6}>
              <SoftTypography
                variant="caption"
                fontWeight={600}
                sx={{ color: "#344767", mb: 0.8, display: "block" }}
              >
                Replication Factor <span style={{ color: "#f5365c" }}>*</span>
              </SoftTypography>
              <SoftInput
                type="number"
                placeholder="1"
                fullWidth
                value={newTopic.replication_factor}
                inputProps={{ min: 1 }}
                onChange={(e) => {
                  setNewTopic((p) => ({ ...p, replication_factor: e.target.value }));
                  setAddErrors((p) => ({ ...p, replication_factor: undefined }));
                }}
                error={!!addErrors.replication_factor}
              />
              {addErrors.replication_factor && (
                <SoftTypography
                  variant="caption"
                  sx={{ color: "#f5365c", mt: 0.5, display: "block" }}
                >
                  {addErrors.replication_factor}
                </SoftTypography>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <SoftButton
            variant="outlined"
            color="secondary"
            onClick={handleAddClose}
            disabled={addLoading}
            sx={{ borderRadius: "10px", px: 2.5 }}
          >
            Cancel
          </SoftButton>
          <SoftButton
            variant="gradient"
            color="info"
            onClick={handleAddSubmit}
            disabled={addLoading}
            sx={{ borderRadius: "10px", px: 3, minWidth: 120 }}
          >
            {addLoading ? (
              <SoftBox display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} thickness={5} sx={{ color: "#fff" }} />
                Creating…
              </SoftBox>
            ) : (
              "Create Topic"
            )}
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm Dialog ── */}
      <Dialog
        open={deleteOpen}
        onClose={handleDeleteClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ pt: 3, pb: 1, px: 3 }}>
          <SoftBox display="flex" alignItems="center" gap={1.2}>
            <SoftBox
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                background: "rgba(245,54,92,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 20, color: "#f5365c" }} />
            </SoftBox>
            <SoftTypography variant="h6" fontWeight="bold">
              Delete Topic
            </SoftTypography>
          </SoftBox>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 1.5, pb: 0.5 }}>
          <DialogContentText sx={{ fontSize: "0.875rem", color: "#67748e" }}>
            Are you sure you want to delete{" "}
            <strong style={{ color: "#344767" }}>{topicToDelete?.name}</strong>? This action cannot
            be undone and all messages in this topic will be permanently lost.
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2.5, gap: 1 }}>
          <SoftButton
            variant="outlined"
            color="secondary"
            onClick={handleDeleteClose}
            disabled={deleteLoading}
            sx={{ borderRadius: "10px", px: 2.5 }}
          >
            Cancel
          </SoftButton>
          <SoftButton
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={deleteLoading}
            sx={{
              borderRadius: "10px",
              px: 3,
              minWidth: 120,
              background: "linear-gradient(135deg, #f5365c 0%, #f56036 100%)",
              color: "#fff",
              "&:hover": { background: "linear-gradient(135deg, #e02d53 0%, #e0502d 100%)" },
            }}
          >
            {deleteLoading ? (
              <SoftBox display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} thickness={5} sx={{ color: "#fff" }} />
                Deleting…
              </SoftBox>
            ) : (
              "Yes, Delete"
            )}
          </SoftButton>
        </DialogActions>
      </Dialog>

      <AlertMessage {...errorState} />
    </DashboardLayout>
  );
}

export default Topics;
